/**
 * How a report opens (RPT-024, RPT-072).
 *
 * - Opened from a link with filters in the address: exactly those, run.
 * - Re-opened: the filters last applied on this report by this person, run
 *   again — the reference re-runs Material Pending straight away.
 * - Opened fresh: the definition's default windows, run only when the report
 *   has no mandatory filters (Financials runs; Material Inward/Outward waits
 *   for Apply even though its period has a default).
 * - The project chosen on one report carries over to the next one opened.
 *
 * Remembered in this browser, per company and per person: a preference, like
 * the column choice, not a shared record (saved views are that).
 */
import { hasMandatoryFilters, type ReportDef } from '@project/contracts'
import { answersRequired, defaultFilterValues } from './filters.js'

export interface MemoryScope {
  readonly companyId: string
  readonly userId: string
}

type Values = Record<string, string>

const lastKey = (s: MemoryScope, report: string): string => `report-last:${s.companyId}:${s.userId}:${report}`
const projectKey = (s: MemoryScope): string => `report-project:${s.companyId}:${s.userId}`

const read = (key: string): string | null => {
  try { return window.localStorage.getItem(key) } catch { return null }
}
const write = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch { /* a private window: nothing is remembered, nothing breaks */ }
}

const clean = (values: Values): Values => {
  const out: Values = {}
  for (const [k, v] of Object.entries(values)) if (typeof v === 'string' && v !== '') out[k] = v
  return out
}

export const recallApplied = (scope: MemoryScope, report: string): Values | null => {
  const raw = read(lastKey(scope, report))
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? clean(parsed as Values) : null
  } catch {
    return null
  }
}

export const currentProject = (scope: MemoryScope): string | null => read(projectKey(scope))

/** What was just applied: remembered for this report, and its project for every report. */
export const rememberApplied = (scope: MemoryScope, report: string, values: Values): void => {
  const kept = clean(values)
  write(lastKey(scope, report), JSON.stringify(kept))
  if (kept['projectId']) write(projectKey(scope), kept['projectId'])
}

export const forgetApplied = (scope: MemoryScope, report: string): void => write(lastKey(scope, report), null)

export interface OpeningState {
  readonly draft: Values
  readonly applied: Values | null
}

/** Whether a report runs when it is opened fresh: its own say-so, else only with nothing mandatory. */
export const runsOnOpen = (def: Pick<ReportDef, 'filters' | 'runOnOpen'>): boolean =>
  def.runOnOpen ?? !hasMandatoryFilters(def.filters)

/** The draft and applied filters a report opens with. */
export const openingState = (
  def: Pick<ReportDef, 'filters' | 'runOnOpen'>,
  query: Values,
  memory: { last: Values | null; project: string | null },
  today: Date = new Date(),
): OpeningState => {
  if (Object.keys(query).length > 0) return { draft: { ...query }, applied: { ...query } }

  const projectFilter = def.filters.find((f) => f.key === 'projectId')
  const carry = (values: Values): Values => {
    if (!projectFilter || !memory.project) return values
    // A single-value project filter takes the first of a carried list.
    const project = projectFilter.multiple ? memory.project : memory.project.split(',')[0] ?? ''
    return project ? { ...values, projectId: project } : values
  }

  if (memory.last) {
    // A select default the remembered filters predate (Show by, Status) is
    // filled in, so a report gaining one does not stop re-running.
    const defaults: Values = {}
    for (const f of def.filters) {
      if (f.control !== 'dateRange' && f.defaultValue && memory.last[f.key] === undefined) defaults[f.key] = f.defaultValue
    }
    const draft = carry({ ...defaults, ...memory.last })
    return { draft, applied: answersRequired(def.filters, draft) ? draft : null }
  }

  const draft = carry(defaultFilterValues(def.filters, today))
  const applied = runsOnOpen(def) && answersRequired(def.filters, draft) ? draft : null
  return { draft, applied }
}
